import get from 'lodash/get';
import uniqBy from 'lodash/uniqBy';
import AnswersPreviewLocation from 'models/location/answers_preview';
import SnippetSearchResult from 'models/search/snippet_search_result';
import { getActiveConversationId } from 'actions/conversation/lib/conversation_helpers';
import SelectFirstAnswerShown from './select_first_answer_shown';
import { getSearchContext, getSearchResultContexts, snowplowTypes } from 'scripts/lib/analytics/domain/snowplow';
import ClearSuggestedReply from 'actions/suggested_replies/clear_suggested_reply';
import RequestSuggestedReply from 'actions/suggested_replies/request_suggested_reply';

export default class HandleAnswerSearchResults {
  constructor(context) {
    this.context = context;
  }

  run({ search }) {
    // The search backend echoes the requested `from`, so a continuation page is any response past offset 0.
    if (get(search, 'query.from') > 0) {
      this.appendPage(search);
      return;
    }

    const answerPanel = this.context.stores.answerPanel.getPending();
    const pendingSearch = get(answerPanel, 'searchResult');
    if (!pendingSearch || !pendingSearch.isLoading || pendingSearch.id !== search.id) {
      return;
    }

    search = this.rankFavoritesFirst(search);
    answerPanel.setSearchResult(search);
    this.context.stores.answerPanel.resetPending();
    this.context.stores.answerPanel.set(answerPanel);
    this.context.executeAction(SelectFirstAnswerShown);

    const currentLocation = this.context.stores.currentLocation.get();
    const customerId = currentLocation.customerId;
    const conversationsStore = this.context.stores.conversations;
    const conversationId = conversationsStore && getActiveConversationId(conversationsStore);
    const compositionId = currentLocation.currentCompositionId;
    const location = currentLocation instanceof AnswersPreviewLocation ? 'Answers Preview' : 'Customer Page';
    const searchText = get(search, 'query.text') || '';
    const resultCount = (search.hits || []).length;

    this.context.analytics.track('Answer Panel Searched', {
      customerId,
      conversationId,
      compositionId,
      location,
      searchText,
      resultCount,
      hasResults: resultCount > 0,
    });

    const snowplowContexts = [getSearchContext(search), ...getSearchResultContexts(search.hits)];
    this.context.analytics.trackSnowplow(
      snowplowTypes.EVENT_ANSWER_SEARCHED,
      { sortType: search.query.sort, language: search.query.languages },
      snowplowContexts
    );

    if (this.context.stores.appFeatures.get().isEnabled('suggestedReplies')) {
      this.context.executeAction(ClearSuggestedReply, { customerId });
      const id = search.id;
      const text = search.query.text;
      const snippets = search.hits;
      const searchLanguage = answerPanel.searchLanguage;
      const searchAudiences = answerPanel.searchAudiences;
      this.context.executeAction(RequestSuggestedReply, { id, text, snippets, searchLanguage, searchAudiences });
    }
  }

  appendPage(search) {
    const answerPanel = this.context.stores.answerPanel.get();
    const currentSearch = get(answerPanel, 'searchResult');
    if (!currentSearch || currentSearch.id !== search.id) {
      return;
    }

    const existingHits = (currentSearch.hits || []).map(hit => hit.toJs());
    const newHits = (search.hits || []).map(hit => hit.toJs());
    const mergedHits = uniqBy([...existingHits, ...newHits], 'id');

    const merged = this.rankFavoritesFirst(
      SnippetSearchResult.fromJs({ ...search.toJs(), hits: mergedHits, isLoadingMore: false })
    );
    answerPanel.setSearchResult(merged);
    this.context.stores.answerPanel.set(answerPanel);
  }

  rankFavoritesFirst(search) {
    if (!this.context.stores.appFeatures.get().isEnabled('favoriteAnswersFirstInSearch')) {
      return search;
    }

    // stable sort preserves relevance order within favorite/non-favorite groups
    const attrs = search.toJs();
    const rank = hit => (this.context.stores.favoriteAnswers.has({ id: hit.id }) ? 0 : 1);
    const hits = [...attrs.hits].sort((a, b) => rank(a) - rank(b));
    return SnippetSearchResult.fromJs({ ...attrs, hits });
  }
}
